import { useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";

/** Long enough for a 0.28s animation, with room for a slow frame. */
const SETTLE_MS = 600;

/**
 * Fades each page in, and then gets out of the way.
 *
 * The class is removed once the animation has finished, and that removal is
 * the whole point of it. `page-in` ends on `transform: translateY(0)` and is
 * declared with `animation-fill-mode: both`, so the element kept that
 * transform for the life of the page — and a transform of anything but `none`
 * makes the element the containing block for every `position: fixed`
 * descendant inside it.
 *
 * Everything routed sits inside this div, so nothing on the site could be
 * fixed to the viewport. The sticky call to action on /lp was laid out ten
 * thousand pixels down the document instead of pinned to the bottom of the
 * screen: invisible, untappable, and reported as "the sticky button does not
 * work on mobile".
 *
 * Removing the class leaves the element in its natural state — opacity 1, no
 * transform — which is exactly where the animation ended, so nothing moves.
 */
const PageTransition = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  const firstRun = useRef(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // First load: the class in the markup below already runs the animation.
    // Later navigations replay it with the Web Animations API. The old way,
    // removing the class and reading offsetWidth to restart it, forced a
    // synchronous layout of the whole page on every route change. An
    // animation started with animate() needs no layout read and, with no
    // fill, leaves no transform behind when it ends.
    if (firstRun.current) {
      firstRun.current = false;
    } else {
      el.classList.remove("page-enter");
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (!reduce && typeof el.animate === "function") {
        el.animate(
          [
            { opacity: 0, transform: "translateY(8px)" },
            { opacity: 1, transform: "translateY(0)" },
          ],
          { duration: 280, easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
        );
      }
      return;
    }

    const settle = () => el.classList.remove("page-enter");
    el.addEventListener("animationend", settle, { once: true });
    // A belt to the braces: reduced motion, a backgrounded tab, or an
    // animation that never fires would otherwise leave the transform in place
    // and take every fixed element on the page down with it.
    const timer = window.setTimeout(settle, SETTLE_MS);

    return () => {
      el.removeEventListener("animationend", settle);
      window.clearTimeout(timer);
    };
  }, [pathname]);

  return <div ref={ref} className="page-enter">{children}</div>;
};

export default PageTransition;
